import { useCallback, useEffect, useState } from 'react'

import { STUDIO_BOT } from '@/constants/blockchain'
import { CustomError } from '@/utils/helpers'
import { get, post } from '@/utils/utils'

import useSpacesAuth from './useSpacesAuth'

// Request / Response types

// Unauthenticated routes

// Size
// POST /feed/size
type SizeRequest = {
  id: string
}

type SizeResponse = {
  valid: boolean
  members: number
  error?: string
}

// Members
// POST /feed/members
type MembersRequest = {
  id: string
}

type MembersResponse = {
  valid: boolean
  members: string[]
  error?: string
}

// Authenticated routes

// Join
// POST /feed/auth/join
type JoinRequest = {
  id: string
  email?: string
}

type JoinResponse = {
  valid: boolean
  error?: string
}

// Leave
// POST /feed/auth/leave
type LeaveRequest = {
  id: string
}

type LeaveResponse = {
  valid: boolean
  error?: string
}

// Joined
// GET /feed/auth/joined

type UserSpace = {
  id: string
  email: string
}

type JoinedResponse = {
  valid: boolean
  joined: UserSpace[]
  error?: string
}

// Email routes

// Set
// POST /feed/auth/email/set
type EmailSetRequest = {
  email: string
}

type EmailSetResponse = {
  valid: boolean
  error?: string
}

// Get
// GET /feed/auth/email/get
type EmailGetResponse = {
  valid: boolean
  email: string
  error?: string
}

// Delete
// POST /feed/auth/email/delete
type EmailDeleteResponse = {
  valid: boolean
  error?: string
}

// Notifications

const useFeed = (spaceId: string) => {
  // Hooks
  const { token } = useSpacesAuth()

  // State
  const [loading, setLoading] = useState<boolean>(false)
  const [size, setSize] = useState<number>(0)
  const [members, setMembers] = useState<string[]>([])
  const [joined, setJoined] = useState<UserSpace[]>([])
  const [defaultEmail, setDefaultEmail] = useState<string>('')
  const [error, setError] = useState<CustomError>({
    call: 'feed',
    msg: '',
    isError: false,
  })

  // Fetch size for space
  const fetchSize = useCallback(async () => {
    try {
      const req: SizeRequest = { id: spaceId }
      const retval = (await post(`${STUDIO_BOT}/feed/size`, JSON.stringify(req))) as SizeResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.size',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        setSize(retval.members)
      }
    } catch (err: any) {
      setError({ call: 'feed.size', msg: err.message, isError: true })
    }
  }, [spaceId])

  // Fetch members for space
  const fetchMembers = useCallback(async () => {
    try {
      const req: MembersRequest = { id: spaceId }
      const retval = (await post(
        `${STUDIO_BOT}/feed/members`,
        JSON.stringify(req)
      )) as MembersResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.members',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        setMembers(retval.members)
      }
    } catch (err: any) {
      setError({ call: 'feed.members', msg: err.message, isError: true })
    }
  }, [spaceId])

  // Fetch joined for address
  const fetchJoined = useCallback(async () => {
    if (!token) return
    try {
      const retval = (await get(`${STUDIO_BOT}/feed/auth/joined`, token)) as JoinedResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.joined',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        setJoined(retval.joined)
      }
    } catch (err: any) {
      setError({ call: 'feed.joined', msg: err.message, isError: true })
    }
  }, [token])

  // Fetch default email
  const fetchEmail = useCallback(async () => {
    if (!token) return
    try {
      const retval = (await get(`${STUDIO_BOT}/feed/auth/email/get`, token)) as EmailGetResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.email.get',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        setDefaultEmail(retval.email)
      }
    } catch (err: any) {
      setError({ call: 'feed.email.get', msg: err.message, isError: true })
    }
  }, [token])

  // Update on changes
  useEffect(() => {
    // Always update size + members when have spaceId
    if (spaceId) {
      const promises = [fetchSize, fetchMembers]
      // Update joined + email when have token
      if (token) {
        promises.push(fetchJoined)
        promises.push(fetchEmail)
      } else {
        // Reset user specific data
        setJoined([])
        setDefaultEmail('')
      }
      setLoading(true)
      Promise.all(promises.map(p => p()))
        .then(() => setLoading(false))
        .catch(() => setLoading(false))
    }
  }, [spaceId, token, fetchSize, fetchMembers, fetchJoined])

  // Join the space
  const joinSpace = useCallback(
    async (userEmail: string = '') => {
      if (!token) return
      // Make request
      try {
        const req: JoinRequest = {
          id: spaceId,
          email: userEmail,
        }
        const retval = (await post(
          `${STUDIO_BOT}/feed/auth/join`,
          JSON.stringify(req),
          token
        )) as JoinResponse
        // Set error if response not valid
        if (!retval.valid) {
          setError({
            call: 'feed.join',
            msg: retval.error || 'Error connecting to Spaces Server',
            isError: true,
          })
        } else {
          // Update data
          fetchJoined()
          fetchSize()
          fetchMembers()
        }
      } catch (err: any) {
        setError({ call: 'feed.join', msg: err.message, isError: true })
      }
    },
    [spaceId, token]
  )

  // Leave the space
  const leaveSpace = useCallback(async () => {
    if (!token) return
    // Make request
    try {
      const req: LeaveRequest = { id: spaceId }
      const retval = (await post(
        `${STUDIO_BOT}/feed/auth/leave`,
        JSON.stringify(req),
        token
      )) as LeaveResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.leave',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        // Update data
        fetchJoined()
        fetchSize()
        fetchMembers()
      }
    } catch (err: any) {
      setError({ call: 'feed.leave', msg: err.message, isError: true })
    }
  }, [spaceId, token])

  // Set default email
  const saveEmail = useCallback(
    async (userEmail: string) => {
      if (!token) return
      // Make request
      try {
        const req: EmailSetRequest = { email: userEmail }
        const retval = (await post(
          `${STUDIO_BOT}/feed/auth/email/set`,
          JSON.stringify(req),
          token
        )) as EmailSetResponse
        // Set error if response not valid
        if (!retval.valid) {
          setError({
            call: 'feed.email.set',
            msg: retval.error || 'Error connecting to Spaces Server',
            isError: true,
          })
        } else {
          // Update email
          setDefaultEmail(userEmail)
        }
      } catch (err: any) {
        setError({ call: 'feed.email.set', msg: err.message, isError: true })
      }
    },
    [token]
  )

  // Delete default email
  const deleteEmail = useCallback(async () => {
    if (!token) return
    // Make request
    try {
      const retval = (await post(
        `${STUDIO_BOT}/feed/auth/email/delete`,
        '',
        token
      )) as EmailDeleteResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'feed.email.delete',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        // Update email
        setDefaultEmail('')
      }
    } catch (err: any) {
      setError({ call: 'feed.email.delete', msg: err.message, isError: true })
    }
  }, [token])

  return {
    size,
    members,
    joined,
    defaultEmail,
    joinSpace,
    leaveSpace,
    saveEmail,
    deleteEmail,
    loading,
    error,
  }
}

export default useFeed
